import { Box } from "@mui/material";
import { VisuallyHidden } from "./VisuallyHidden";

/**
 * The four states any data-backed surface in the portal can be in. `ready` means "the data
 * arrived and there is something to show" — it renders nothing, because the content itself
 * is the message.
 */
export type AsyncState = "loading" | "error" | "empty" | "ready";

interface AsyncStatusProps {
	state: AsyncState;
	/** Announced while `state === "loading"`. Name the thing being loaded, not just "Loading". */
	loadingText: string;
	/** Announced when the load succeeded with zero results. */
	emptyText?: string;
	/** Announced when the load failed. Include the reason when one is available. */
	errorText?: string;
	/**
	 * Render the message on screen as well. Off by default: most callers already paint a
	 * skeleton or an empty illustration and only need the text equivalent.
	 */
	visible?: boolean;
}

/** Text for the state, or `null` when there is nothing to announce. */
function messageFor(props: AsyncStatusProps): string | null {
	switch (props.state) {
		case "loading":
			return props.loadingText;
		case "empty":
			return props.emptyText ?? null;
		case "error":
			return props.errorText ?? null;
		case "ready":
			return null;
	}
}

/**
 * Makes loading / empty / error distinguishable *in text* for anything reading the
 * accessibility tree — which is what browser agents read, and all a screen reader gets.
 *
 * A spinner alone is indistinguishable from an empty list, and an empty list is
 * indistinguishable from a failed fetch: all three are "no rows" to an agent. This renders
 * the distinguishing sentence into a live region so the state is never inferred from pixels.
 *
 * Errors use `role="alert"` (assertive — the user's action failed and they need to know now);
 * loading and empty use `role="status"` (polite — informational, must not interrupt).
 *
 * Pair with `aria-busy` on the region that owns the content, so an agent can tell "still
 * working" from "done, and this is the answer":
 *
 * ```tsx
 * <Box aria-busy={state === "loading"}>
 *   <AsyncStatus state={state} loadingText="Loading jobs…" emptyText="No jobs found" />
 *   {rows}
 * </Box>
 * ```
 */
export function AsyncStatus(props: AsyncStatusProps) {
	const message = messageFor(props);
	if (message === null) return null;

	const isError = props.state === "error";
	const role = isError ? "alert" : "status";
	// `alert` is implicitly assertive, `status` implicitly polite; stated explicitly because
	// some agents read the attribute rather than deriving it from the role.
	const live = isError ? "assertive" : "polite";

	if (props.visible) {
		return (
			<Box role={role} aria-live={live}>
				{message}
			</Box>
		);
	}
	return (
		<Box component="span" role={role} aria-live={live}>
			<VisuallyHidden>{message}</VisuallyHidden>
		</Box>
	);
}
